import { Text, Stack, Flex, useDisclosure, Box } from '@chakra-ui/react';
import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CellValue, Cell } from 'react-table';

import Button from 'components/Button';
import CustomFieldModal from 'components/CustomFieldModal';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import Table, { IndeterminateCheckbox } from 'components/Table';
import Title from 'components/Title';
import { getRoundUpTime } from 'containers/EmployeeTimeClock/shared/helper';
import FEATURES from 'global/features';
import { routePaths } from 'global/routePaths';
import { ICustomFieldItem } from 'hooks/customField/useFetchPaginatedCustomFields';
import { fetchPaginatedJobs } from 'hooks/jobs/useFetchPaginatedJobs';
import { fetchPaginatedTasks } from 'hooks/task/useFetchPaginatedTasks';
import { IBulkClockin, useBulkClockIn } from 'hooks/timeClock/useBulkClockIn';
import { IBulkClockOut, useBulkClockOut } from 'hooks/timeClock/useBulkClockOut';
import {
  IQuickTimeEntriesSearchQuery,
  useFetchPaginatedQuickTimeEntries,
} from 'hooks/timesheet/useFetchQuickTimeEntries';
import { useFeatureList } from 'store/useFeatureStore';
import { useUserObject } from 'store/useUserStore';
import { hasCustomFieldAccess, transformCustomFieldsToPostData } from 'utils/customFieldsUtils';
import { getAsyncSelectLoadOptions, getTimezoneOffsetInHM } from 'utils/helpers';

const QuickTimeEntry: React.FC<unknown> = () => {
  const { t } = useTranslation(['common', 'timesheetMileage']);

  const customFieldAccess = hasCustomFieldAccess();

  const userStore = useUserObject();
  const featureList: any = useFeatureList();

  const [jobTaskValues, setJobTaskValues] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState<IQuickTimeEntriesSearchQuery>({
    page: 1,
    page_size: 25,
  });
  const [dialogIds, setDialogIds] = useState<{ user_ids: Array<any> }>({ user_ids: [] });
  const [selectedJobTask, setSelectedJobTask] = useState<{ job: any; task: any }>({ job: null, task: null });
  const [selectedBulkJobTasks, setSelectedBulkJobTasks] = useState<{ job: any; task: any }>({ job: null, task: null });

  const { isOpen: isClockInModalOpen, onOpen: onClockInModalOpen, onClose: onClockInModalClose } = useDisclosure();
  const { isOpen: isClockOutModalOpen, onOpen: onClockOutModalOpen, onClose: onClockOutModalClose } = useDisclosure();

  const { data, status, refetch } = useFetchPaginatedQuickTimeEntries(searchQuery);
  const { status: bulkClockInStatus, mutateAsync: mutateAsyncBulkClockIn } = useBulkClockIn();
  const { status: bulkClockOutStatus, mutateAsync: mutateAsyncBulkClockOut } = useBulkClockOut();

  const navigate = useNavigate();

  useEffect(() => {
    if (!featureList[FEATURES.jobs]) {
      navigate(routePaths.timesheetAndMileage.base.route);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} />;
      },
    },
    {
      Header: t('common:name'),
      accessor: 'firstName',
      Cell: (data: CellValue) => (
        <Stack direction="column" spacing="0.5">
          <Text fontWeight="semibold" color="primary.500">
            {data.value} {data.row.original?.lastName}
          </Text>
          <Text fontSize="xs" color="gray.500" mt="-1">
            {data.row.original?.clocked_status ? t('timesheetMileage:clockedIn') : t('timesheetMileage:clockedOut')}
          </Text>
        </Stack>
      ),
    },
    {
      Header: t('common:email'),
      accessor: 'email',
      Cell: (data: CellValue) => <Text>{data.value || '- '}</Text>,
    },
    {
      Header: t('common:job'),
      accessor: 'selected_job',
      width: 280,
      Cell: (data: Cell<any>) => {
        return (
          <ReactSelectAsyncPaginate
            loadOptions={(search: any, _: any, { page }: any) => jobLoadOptions(search, page, data.row.original.userId)}
            additional={{
              page: 1,
            }}
            value={
              data.row.original?.clocked_status
                ? data.row.original.selected_job || null
                : jobTaskValues &&
                    jobTaskValues[`${data.row.original.userId}`] &&
                    jobTaskValues[`${data.row.original.userId}`].job
                  ? jobTaskValues[`${data.row.original.userId}`].job
                  : null
            }
            onChange={(value: any) => {
              setJobTaskValues((prevValue: any) => ({
                ...prevValue,
                [data.row.original.userId]: {
                  job: value || null,
                },
              }));
            }}
            size="md"
            variant="filled"
            fontSize="sm"
            debounceTimeout={300}
            isClearable
            menuPortalTarget={document.body}
            isSearchable
            placeholder={t('common:selectJob')}
            getOptionLabel={(option: any) => option.name}
            getOptionValue={(option: any) => option.id}
            isDisabled={data.row.original?.clocked_status === 1}
          />
        );
      },
    },
    {
      Header: 'Task',
      accessor: 'selected_task',
      width: 280,
      Cell: (data: CellValue) => {
        return (
          <ReactSelectAsyncPaginate
            loadOptions={(search: any, _: any, { page }: any) =>
              taskLoadOptions(
                page,
                search,
                jobTaskValues &&
                  jobTaskValues[`${data.row.original.userId}`] &&
                  jobTaskValues[`${data.row.original.userId}`].job
                  ? jobTaskValues[`${data.row.original.userId}`].job?.id
                  : null
              )
            }
            additional={{
              page: 1,
            }}
            value={
              data.row.original?.clocked_status
                ? data.row.original?.selected_task || null
                : jobTaskValues &&
                    jobTaskValues[`${data.row.original.userId}`] &&
                    jobTaskValues[`${data.row.original.userId}`].task
                  ? jobTaskValues[`${data.row.original.userId}`].task
                  : null
            }
            onChange={(value: any) => {
              setJobTaskValues((prevValue: any) => ({
                ...prevValue,
                [data.row.original.userId]: {
                  ...(prevValue && prevValue[`${data.row.original.userId}`]
                    ? prevValue[`${data.row.original.userId}`]
                    : {}),
                  task: value || null,
                },
              }));
            }}
            size="md"
            variant="filled"
            fontSize="sm"
            debounceTimeout={300}
            isClearable
            menuPortalTarget={document.body}
            isSearchable
            placeholder={t('timesheetMileage:selectTask')}
            getOptionLabel={(option: any) => option.name}
            getOptionValue={(option: any) => option.id}
            isDisabled={data.row.original?.clocked_status === 1}
            cacheUniqs={[jobTaskValues]}
          />
        );
      },
    },
    {
      Header: '',
      accessor: 'action',
      width: 180,
      Cell: (data: Cell<any>) => {
        return data.row.original?.clocked_status ? (
          <Button
            bg="rgba(238, 112, 56, 0.1)"
            _hover={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            _active={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            color="secondary.300"
            onClick={() => handleSingleClockOut(data.row.original)}
          >
            {t('common:clockOut')}
          </Button>
        ) : (
          <Button
            bg="rgba(238, 112, 56, 0.1)"
            _hover={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            _active={{ bg: 'rgba(238, 112, 56, 0.2)' }}
            color="secondary.300"
            isDisabled={
              userStore?.companyInfo?.requiresJob
                ? jobTaskValues &&
                  jobTaskValues[`${data.row.original.userId}`] &&
                  jobTaskValues[`${data.row.original.userId}`].job
                  ? false
                  : true
                : false
            }
            onClick={() => handleSingleClockIn(data.row.original.userId)}
          >
            {t('common:clockIn')}
          </Button>
        );
      },
    },
  ];

  const jobLoadOptions = async (search: string, page: any, userId: number | number[]) => {
    return getAsyncSelectLoadOptions(fetchPaginatedJobs, { search, page, job_status: 1, userId });
  };

  const taskLoadOptions = (page: number, search: string, jobId: any = '0') => {
    return getAsyncSelectLoadOptions(fetchPaginatedTasks, { page, search, task_status: 1, jobId });
  };

  const handlePageChange = useCallback((selectedPageIndex: number) => {
    const updatedSearchQuery = (previousSearchQuery: IQuickTimeEntriesSearchQuery) => ({
      ...previousSearchQuery,
      page: selectedPageIndex + 1,
    });
    setSearchQuery(updatedSearchQuery);
  }, []);

  const handleSingleClockIn = (userId: number) => {
    const jobTaskObj: any = jobTaskValues ? jobTaskValues[`${userId}`] : undefined;

    setDialogIds({ user_ids: [userId] });
    setSelectedJobTask({ job: jobTaskObj?.job || null, task: jobTaskObj?.task || null });
    onClockInModalOpen();
  };

  const handleBulkClockIn = (selectedFlatRows: any) => {
    const ids = selectedFlatRows
      .filter((row: any) => !row.original.clocked_status)
      .map((row: any) => row.original.userId);
    setDialogIds({ user_ids: ids });
    setSelectedJobTask({ job: selectedBulkJobTasks.job || null, task: selectedBulkJobTasks.task || null });
    onClockInModalOpen();
  };

  const clockInUsers = (customFieldsData: Array<ICustomFieldItem> | null, values: Record<string, any>) => {
    const postData: IBulkClockin = {
      ...dialogIds,
      job_id: selectedJobTask.job?.id || null,
      task_id: selectedJobTask.task?.id || null,
      clock_in_time: getRoundUpTime('clock_in'),
      offset: getTimezoneOffsetInHM(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      source: 'quick-time-clock',
      ...(customFieldsData ? { custom_fields: transformCustomFieldsToPostData(customFieldsData, values) } : {}),
    };
    mutateAsyncBulkClockIn(postData).then(() => {
      setDialogIds({ user_ids: [] });
      onClockInModalClose();
      setJobTaskValues(null);
      setSelectedJobTask({ job: null, task: null });
      setSelectedBulkJobTasks({ job: null, task: null });
      refetch();
    });
  };

  // Clock out Users
  const handleSingleClockOut = (userData: any) => {
    setDialogIds({ user_ids: [userData?.userId] });
    setSelectedJobTask({ job: userData?.selected_job || null, task: userData?.selected_task || null });
    onClockOutModalOpen();
  };

  const handleBulkClockOut = (selectedFlatRows: any) => {
    const ids = selectedFlatRows
      .filter((row: any) => row.original.clocked_status)
      .map((row: any) => row.original.userId);
    setDialogIds({ user_ids: ids });
    setSelectedJobTask({ job: null, task: null });
    onClockOutModalOpen();
  };

  const clockOutUsers = (customFieldsData: Array<ICustomFieldItem> | null, values: Record<string, any>) => {
    const postData: IBulkClockOut = {
      ...dialogIds,
      clock_out_time: getRoundUpTime('clock_out'),
      offset: getTimezoneOffsetInHM(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      source: 'quick-time-clock',
      ...(Array.isArray(customFieldsData)
        ? { custom_fields: transformCustomFieldsToPostData(customFieldsData, values) }
        : {}),
    };

    mutateAsyncBulkClockOut(postData).then(() => {
      setDialogIds({ user_ids: [] });
      onClockOutModalClose();
      setJobTaskValues(null);
      setSelectedJobTask({ job: null, task: null });
      setSelectedBulkJobTasks({ job: null, task: null });
      refetch();
    });
  };

  return (
    <>
      <CustomFieldModal
        jobId={selectedJobTask.job?.id || null}
        type="clock_in"
        isOpen={isClockInModalOpen}
        onClose={onClockInModalClose}
        title={t('timesheetMileage:bulkClockInModalTitle', {
          user: dialogIds.user_ids.length === 1 ? 'user' : 'users',
        })}
        subTitle={t('timesheetMileage:bulkClockInModalSubTitle', {
          user: dialogIds.user_ids.length === 1 ? 'user' : 'users',
        })}
        isConfirmButtonLoading={bulkClockInStatus === 'loading'}
        handleConfirmButtonClick={clockInUsers}
      />

      <CustomFieldModal
        jobId={selectedJobTask.job?.id || null}
        type="clock_out"
        isOpen={isClockOutModalOpen}
        onClose={onClockOutModalClose}
        title={t('timesheetMileage:bulkClockOutModalTitle', {
          user: dialogIds.user_ids.length === 1 ? 'this user' : 'these users',
        })}
        subTitle={t('timesheetMileage:bulkClockOutModalSubTitle', {
          user: dialogIds.user_ids.length === 1 ? 'this user' : 'these users',
        })}
        isConfirmButtonLoading={bulkClockOutStatus === 'loading'}
        handleConfirmButtonClick={clockOutUsers}
      />
      <Title>{t('timesheetMileage:quickTimeClock')}</Title>
      <Table
        columns={columns}
        data={data?.payload || []}
        paginationData={data?.meta_data?.pagination}
        isLoading={status === 'loading'}
        enableRowSelect
        onPageChange={handlePageChange}
        renderTableFooter={(selectedFlatRows: any) => {
          return (
            <Flex
              width="100%"
              flexWrap="wrap"
              justifyContent="space-between"
              alignItems="center"
              border="1px"
              borderColor="primary.100"
              boxShadow="0 -4px 4px -1px rgba(0, 0, 0, 0.1),0 -2px 2px -1px rgba(0, 0, 0, 0.06);"
              px="6"
              py="4"
            >
              <Text fontWeight="semibold">
                {selectedFlatRows.length} {t('common:selected')}
              </Text>
              <Stack direction="row" spacing="3" flex="1 0 auto" justifyContent="flex-end">
                <Box flex="0 1 180px">
                  <ReactSelectAsyncPaginate
                    loadOptions={(search: any, _: any, { page }: any) => {
                      const userIds = selectedFlatRows.length
                        ? selectedFlatRows
                            .filter((row: any) => !row.original.clocked_status)
                            .map((item: any) => item.original.userId)
                            .join(',')
                        : '';

                      return jobLoadOptions(search, page, userIds);
                    }}
                    additional={{
                      page: 1,
                    }}
                    value={selectedBulkJobTasks.job || null}
                    onChange={(value: any) => {
                      setSelectedBulkJobTasks({ job: value, task: null });
                    }}
                    size="md"
                    variant="filled"
                    fontSize="sm"
                    debounceTimeout={300}
                    isClearable
                    menuPortalTarget={document.body}
                    isSearchable
                    placeholder={t('common:selectJob')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option.id}
                    menuPlacement="top"
                  />
                </Box>
                <Box flex="0 1 180px">
                  <ReactSelectAsyncPaginate
                    loadOptions={(search: any, _: any, { page }: any) =>
                      taskLoadOptions(page, search, selectedBulkJobTasks?.job?.id || null)
                    }
                    additional={{
                      page: 1,
                    }}
                    value={selectedBulkJobTasks.task || null}
                    onChange={(value: any) => {
                      setSelectedBulkJobTasks((prevValue: any) => ({
                        ...prevValue,
                        task: value,
                      }));
                    }}
                    size="md"
                    variant="filled"
                    fontSize="sm"
                    debounceTimeout={300}
                    isClearable
                    menuPortalTarget={document.body}
                    isSearchable
                    placeholder={t('timesheetMileage:selectTask')}
                    getOptionLabel={(option: any) => option.name}
                    getOptionValue={(option: any) => option.id}
                    cacheUniqs={[selectedBulkJobTasks.job]}
                    menuPlacement="top"
                  />
                </Box>
                <Button
                  bg="rgba(238, 112, 56, 0.1)"
                  _hover={{ bg: 'rgba(238, 112, 56, 0.2)' }}
                  _active={{ bg: 'rgba(238, 112, 56, 0.2)' }}
                  color="secondary.300"
                  isDisabled={userStore?.companyInfo?.requiresJob ? (selectedBulkJobTasks.job ? false : true) : false}
                  onClick={() => handleBulkClockIn(selectedFlatRows)}
                >
                  {t('common:clockIn')}
                </Button>
                {!customFieldAccess && (
                  <Button
                    bg="rgba(238, 112, 56, 0.1)"
                    _hover={{ bg: 'rgba(238, 112, 56, 0.2)' }}
                    _active={{ bg: 'rgba(238, 112, 56, 0.2)' }}
                    color="secondary.300"
                    onClick={() => handleBulkClockOut(selectedFlatRows)}
                  >
                    {t('common:clockOut')}
                  </Button>
                )}
              </Stack>
            </Flex>
          );
        }}
      />
    </>
  );
};

export default QuickTimeEntry;
